import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Folder, X, MapPin, Calendar, Copy, Check, ExternalLink, Ban, GitBranch, AlertTriangle, Trash2 } from 'lucide-react';
import { useData } from '../contexts/DataContext';
import StatusBadge from '../components/common/StatusBadge';
import { truncateHash } from '../components/common/CertificateCard';
import { reportValidationUrl, reportOwnerOpenUrl } from '../utils/site';

const STATUS_FILTERS = [
  { id: 'all', label: 'Todos' },
  { id: 'signed', label: 'Registrados' },
  { id: 'draft', label: 'Rascunhos' },
];

const validationLink = (id) => reportValidationUrl(id);
const ownerOpenLink = (id, token) => reportOwnerOpenUrl(id, token);

const CopyButton = ({ value, label }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard indisponível */ }
  };
  return (
    <button onClick={copy} className="inline-flex items-center gap-1 text-navy hover:text-navy-2 transition-colors" title={`Copiar ${label}`} aria-label={`Copiar ${label}`}>
      {copied ? <Check className="w-3.5 h-3.5 text-selo" aria-hidden="true" /> : <Copy className="w-3.5 h-3.5" aria-hidden="true" />}
    </button>
  );
};

const History = () => {
  const { reports, folders, hasFeature, createFolder, deleteFolder, updateReportFolder, deleteDraftReport, revokeReport } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFolder, setActiveFolder] = useState('all');
  const [activeStatus, setActiveStatus] = useState('all');
  const [activeLocation, setActiveLocation] = useState('all');
  const [sortOrder, setSortOrder] = useState('recent');
  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [revokingReport, setRevokingReport] = useState(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [isRevoking, setIsRevoking] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const handleDeleteDraft = async (report) => {
    if (report.status !== 'draft') return;
    if (!window.confirm(`Excluir o rascunho de ${report.patient_name || 'paciente sem nome'}? Esta ação não pode ser desfeita.`)) return;
    setDeletingId(report.id);
    try {
      await deleteDraftReport(report.id);
    } catch (err) {
      alert('Erro ao excluir rascunho: ' + err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleRevoke = async (e) => {
    e.preventDefault();
    if (!revokeReason.trim()) return;
    setIsRevoking(true);
    try {
      await revokeReport(revokingReport.id, revokeReason);
      setRevokingReport(null);
      setRevokeReason('');
    } catch (err) {
      alert('Erro ao revogar: ' + err.message);
    } finally {
      setIsRevoking(false);
    }
  };

  const canMidFilters = hasFeature('advanced_filters');
  const canAdvFilters = hasFeature('folders');

  const locationsInReports = useMemo(() => {
    const map = new Map();
    reports.forEach(r => { if (r.locations?.name) map.set(r.location_id, r.locations.name); });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [reports]);

  const filteredReports = useMemo(() => {
    let list = reports.filter(r => {
      const matchesSearch = r.patient_name?.toLowerCase().includes(searchTerm.toLowerCase()) || r.patient_owner?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFolder = !canAdvFilters || activeFolder === 'all' || r.folder_id === activeFolder;
      const matchesStatus = !canMidFilters || activeStatus === 'all' || r.status === activeStatus;
      const matchesLocation = !canMidFilters || activeLocation === 'all' || r.location_id === activeLocation;
      return matchesSearch && matchesFolder && matchesStatus && matchesLocation;
    });
    if (canMidFilters) {
      list = [...list].sort((a, b) => {
        const da = new Date(a.created_at).getTime();
        const db = new Date(b.created_at).getTime();
        return sortOrder === 'recent' ? db - da : da - db;
      });
    }
    return list;
  }, [reports, searchTerm, activeFolder, activeStatus, activeLocation, sortOrder, canMidFilters, canAdvFilters]);

  const handleCreateFolder = async (e) => {
    e.preventDefault();
    if (!newFolderName.trim()) return;
    await createFolder(newFolderName);
    setNewFolderName('');
    setIsFolderModalOpen(false);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
        <div>
          <h1 className="font-display text-3xl font-bold text-navy tracking-tight">Meus laudos</h1>
        </div>
        <Link to="/app/novo-laudo" className="btn-convert">Novo laudo</Link>
      </div>

      {canAdvFilters && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[11px] font-semibold text-muted uppercase tracking-widest">Suas pastas</h2>
            <button onClick={() => setIsFolderModalOpen(true)} className="text-[11px] font-semibold text-navy uppercase tracking-widest hover:underline min-h-[44px]">+ Criar pasta</button>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-4 no-scrollbar">
            <button onClick={() => setActiveFolder('all')} className={`flex items-center gap-2 px-4 py-2.5 min-h-[44px] rounded-card font-semibold text-sm transition-colors whitespace-nowrap ${activeFolder === 'all' ? 'bg-navy text-white' : 'bg-paper border border-line text-muted hover:border-navy/40'}`}><Folder className="w-4 h-4" aria-hidden="true" /> Todos</button>
            {folders.map(folder => (
              <button key={folder.id} onClick={() => setActiveFolder(folder.id)} className={`flex items-center gap-2 px-4 py-2.5 min-h-[44px] rounded-card font-semibold text-sm transition-colors whitespace-nowrap ${activeFolder === folder.id ? 'bg-navy text-white' : 'bg-paper border border-line text-muted hover:border-navy/40'}`}>
                <Folder className="w-4 h-4" aria-hidden="true" /> {folder.name}
                <span role="button" tabIndex={0} title="Excluir pasta" onClick={(e) => { e.stopPropagation(); deleteFolder(folder.id); }} onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); deleteFolder(folder.id); } }} className="ml-1 opacity-60 hover:opacity-100"><X className="w-3.5 h-3.5" aria-hidden="true" /></span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="relative mb-4">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted w-5 h-5" aria-hidden="true" />
        <label htmlFor="report-search" className="sr-only">Buscar por paciente ou tutor</label>
        <input id="report-search" type="text" placeholder="Buscar por paciente ou tutor..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="input-field pl-12 py-4" />
      </div>

      {canMidFilters ? (
        <div className="flex flex-wrap items-center gap-3 mb-8">
          {STATUS_FILTERS.map(f => <button key={f.id} onClick={() => setActiveStatus(f.id)} className={`px-4 py-2 min-h-[44px] rounded-card text-sm font-semibold transition-colors ${activeStatus === f.id ? 'bg-navy text-white' : 'bg-paper border border-line text-muted hover:border-navy/40'}`}>{f.label}</button>)}
          <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} className="input-field w-auto text-sm py-2 ml-auto" aria-label="Ordenar laudos"><option value="recent">Mais recentes</option><option value="oldest">Mais antigos</option></select>
          {locationsInReports.length > 0 && <select value={activeLocation} onChange={(e) => setActiveLocation(e.target.value)} className="input-field w-auto text-sm py-2" aria-label="Filtrar por local"><option value="all">Todos os locais</option>{locationsInReports.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}</select>}
        </div>
      ) : (
        <div className="flex items-center gap-2 mb-8 text-xs text-muted bg-paper-alt border border-line rounded-card px-4 py-3">Filtros por status, data e local fazem parte do plano Rotina.<Link to="/app/planos" className="text-navy font-semibold hover:underline">Fazer upgrade</Link></div>
      )}

      {filteredReports.length === 0 ? (
        <div className="bg-paper rounded-card border border-line shadow-card flex flex-col items-center justify-center py-20 text-center px-4">
          <p className="text-sm font-semibold text-ink mb-1">{reports.length === 0 ? 'Nenhum laudo ainda' : 'Nenhum laudo corresponde aos filtros'}</p>
          <p className="text-sm text-muted mb-6 max-w-xs">{reports.length === 0 ? 'Crie seu primeiro laudo a partir de um modelo e ele aparecerá aqui.' : 'Ajuste a busca ou os filtros para encontrar o laudo.'}</p>
          {reports.length === 0 && <Link to="/app/novo-laudo" className="btn-convert">Criar meu primeiro laudo</Link>}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredReports.map((report) => {
            const isSigned = report.status === 'signed';
            return (
              <div key={report.id} className="bg-paper rounded-card border border-line shadow-card p-6 hover:border-navy/40 transition-colors">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div className="min-w-0">
                    <div className="min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="font-semibold text-ink text-base truncate">{report.patient_name || 'Paciente sem nome'}</h3>
                        <StatusBadge status={isSigned ? 'registered' : report.status === 'revoked' ? 'error' : 'draft'} label={report.status === 'revoked' ? 'Revogado' : undefined} />
                        {report.version > 1 && <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-navy bg-paper-alt border border-line rounded-md px-2 py-0.5"><GitBranch className="w-3 h-3" aria-hidden="true" /> v{report.version}</span>}
                        {report.superseded_at && <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-muted bg-paper-alt border border-line rounded-md px-2 py-0.5">Substituído</span>}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted mt-1.5 flex-wrap">
                        {report.patient_owner && <span>Tutor: {report.patient_owner}</span>}
                        <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" aria-hidden="true" /> {report.locations?.name || 'Sem local'}</span>
                        <span className="flex items-center gap-1 tabular-nums"><Calendar className="w-3.5 h-3.5" aria-hidden="true" /> {report.exam_date || 'Sem data'}</span>
                        {report.hash && <span className="font-mono">SHA-256 · {truncateHash(report.hash)}</span>}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 shrink-0">
                    {canAdvFilters && <select value={report.folder_id || ''} onChange={(e) => updateReportFolder(report.id, e.target.value || null)} className="input-field w-auto text-xs font-semibold uppercase tracking-wide text-muted py-2" aria-label="Mover laudo para pasta"><option value="">Mover para...</option>{folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}</select>}
                    {report.status !== 'revoked' && <Link to={`/app/editar-laudo/${report.id}`} className="btn-secondary text-xs uppercase tracking-widest">{isSigned ? 'Revisar' : 'Continuar'}</Link>}
                    {report.status === 'draft' && (
                      <button onClick={() => handleDeleteDraft(report)} disabled={deletingId === report.id} title="Excluir rascunho" aria-label={`Excluir rascunho de ${report.patient_name || 'paciente sem nome'}`} className="p-2.5 min-h-[44px] min-w-[44px] text-muted hover:text-danger hover:bg-danger-tint rounded-card transition-colors disabled:opacity-50">
                        <Trash2 className="w-4 h-4" aria-hidden="true" />
                      </button>
                    )}
                    {isSigned && <button onClick={() => setRevokingReport(report)} title="Revogar laudo" aria-label={`Revogar laudo de ${report.patient_name || 'paciente sem nome'}`} className="p-2.5 min-h-[44px] min-w-[44px] text-muted hover:text-danger hover:bg-danger-tint rounded-card transition-colors"><Ban className="w-4 h-4" aria-hidden="true" /></button>}
                    {(isSigned || report.status === 'revoked') && <a href={ownerOpenLink(report.id, report.access_token)} target="_blank" rel="noopener noreferrer" className="btn-primary text-xs uppercase tracking-widest"><ExternalLink className="w-4 h-4" aria-hidden="true" /> Abrir</a>}
                  </div>
                </div>

                {(isSigned || report.status === 'revoked') && report.access_token && (
                  <div className="mt-4 pt-4 border-t border-line flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 text-xs">
                    <div className="flex items-center gap-2"><span className="text-muted">Senha do paciente:</span><span className="font-mono font-semibold text-ink tracking-widest">{report.access_token}</span><CopyButton value={report.access_token} label="senha" /></div>
                    <div className="flex items-center gap-2 min-w-0"><span className="text-muted shrink-0">Link:</span><span className="font-mono text-navy truncate">{validationLink(report.id)}</span><CopyButton value={validationLink(report.id)} label="link de validação" /></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <AnimatePresence>
        {isFolderModalOpen && (
          <div className="fixed inset-0 bg-navy/50 backdrop-blur-[2px] z-[100] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} className="bg-paper rounded-card p-8 w-full max-w-md shadow-xl">
              <div className="flex justify-between items-center mb-8"><h2 className="font-display text-2xl font-bold text-navy tracking-tight">Nova pasta</h2><button onClick={() => setIsFolderModalOpen(false)} className="p-2 min-h-[44px] min-w-[44px] text-muted hover:text-ink transition-colors" aria-label="Fechar"><X className="w-6 h-6" aria-hidden="true" /></button></div>
              <form onSubmit={handleCreateFolder} className="space-y-6">
                <div><label htmlFor="folder-name" className="block text-[11px] font-semibold text-muted uppercase tracking-widest mb-2">Nome da pasta</label><input id="folder-name" autoFocus type="text" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} className="input-field" placeholder="Ex: Urgências, Retornos..." /></div>
                <button type="submit" className="btn-primary w-full uppercase tracking-widest text-xs">Criar pasta</button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {revokingReport && (
          <div className="fixed inset-0 bg-navy/50 backdrop-blur-[2px] z-[100] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} className="bg-paper rounded-card p-8 w-full max-w-md shadow-xl">
              <div className="flex justify-between items-center mb-4"><h2 className="font-display text-2xl font-bold text-navy tracking-tight">Revogar laudo</h2><button onClick={() => { setRevokingReport(null); setRevokeReason(''); }} className="p-2 min-h-[44px] min-w-[44px] text-muted hover:text-ink transition-colors" aria-label="Fechar"><X className="w-6 h-6" aria-hidden="true" /></button></div>
              <div className="flex items-start gap-3 bg-danger-tint border border-danger/25 text-danger rounded-card px-4 py-3 mb-6 text-sm"><AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" aria-hidden="true" /><p>Revogar é <strong>definitivo</strong> e não pode ser desfeito. O laudo de <strong>{revokingReport.patient_name || 'paciente sem nome'}</strong> continuará acessível pelo link e senha já compartilhados, mas com um aviso explícito de revogação. Para corrigir informações, prefira "Revisar" (gera uma nova versão).</p></div>
              <form onSubmit={handleRevoke} className="space-y-6">
                <div><label htmlFor="revoke-reason" className="block text-[11px] font-semibold text-muted uppercase tracking-widest mb-2">Motivo da revogação *</label><textarea id="revoke-reason" autoFocus required rows={3} value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)} className="input-field resize-none" placeholder="Ex: exame duplicado, paciente incorreto, solicitação do responsável..." /></div>
                <button type="submit" disabled={isRevoking || !revokeReason.trim()} className="w-full py-4 rounded-card text-xs font-semibold uppercase tracking-widest bg-danger text-white hover:bg-danger/90 transition-colors disabled:opacity-50">{isRevoking ? 'Revogando...' : 'Confirmar revogação'}</button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default History;
